import React, { memo, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Typography,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
} from "@mui/material";
import style from "../../styles/brsr/general.module.css";
import { getRowErrorMessages } from "../../utils/getRowErrorMessage";
import {
  validateNumber,
  validateNumberInput,
  validatePercentage,
  validatePercentageInput,
} from "../../utils/validateFields";

const TableData = memo(
  ({
    inputData,
    subHeading,
    tables,
    setTables,
    errors,
    setErrors,
    defaultSegments,
    name,
    defaultColumn,
    defaultWidth,
    disabled,
  }) => {
    console.log(tables);

    const [snackOpen, setsnackOpen] = useState({
      open: false,
      message: "",
      severity: "",
    });

    const handleInputChange = (
      e,
      fieldName,
      fieldTitle,
      field,
      title,
      fieldType,
      options
    ) => {
      const { value } = e.target; // Get the input value
      // Validate percentage and number fields
      if (fieldType === "percentage" && !validatePercentageInput(value)) return;
      if (fieldType === "number" && value!=="" &&  !validateNumberInput(value)) return;

      // Clear error if the value is valid
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: {
          ...prevErrors[name],
          [title]: {
            ...prevErrors[name]?.[title],
            [fieldTitle]: "",
          },
        },
      }));

      let value2 = options?.includes("Select All")
        ? value?.includes("Select All")
          ? value?.length < options?.length && value?.length !== 1
            ? value?.[value?.length - 1] === "Select All"
              ? options
              : value?.filter((el) => el !== "Select All")
            : options
          : value?.length ===
            options?.filter((el) => el !== "Select All").length
          ? []
          : value
        : value;

      setTables((prevTables) => ({
        ...prevTables,
        [field]: {
          ...prevTables[field],
          [fieldName]:
            fieldType === "radio" ? value === "true" : value2 ? value2 : value,
        },
      }));
    };

    const getSegmentErrorMessages = (
      defaultSegments,
      inputData,
      tables,
      errors
    ) => {
      const rowErrorMessages = [];

      // Loop through each row to create error messages for missing fields
      defaultSegments.forEach((segment, rowIndex) => {
        const rowErrors = errors?.[segment?.title];

        if (rowErrors) {
          const missingFields = Object.entries(rowErrors)
            .filter(
              ([_, errorMessage]) => errorMessage && errorMessage.trim() !== ""
            ) // Filter non-empty errors
            .map(([fieldTitle, errorMessage]) => `${fieldTitle}`)
            .join(", ");

          if (missingFields.length > 0) {
            // Only push if there are valid missing fields
            rowErrorMessages.push(
              `Row ${Number(rowIndex) + 1}: Missing fields - ${missingFields}`
            );
          }
        }
      });
      return rowErrorMessages;
    };

    const segmentErrorMessages = getSegmentErrorMessages(
      defaultSegments,
      inputData,
      tables,
      errors
    );

    return (
      <Box sx={{ my: "1rem" }}>
        {subHeading && <p className={style.subHeading}>{subHeading}</p>}
        {inputData?.map((section, tableIndex) => (
          <div key={section.fieldName} sx={{ marginBottom: "2rem" }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontStyle: "Inter",
                fontSize: "16px",
                lineHeight: "24px",
                my: 2,
                textIndent: "-1rem",
                paddingLeft: "0.8rem",
              }}
            >
              {section.title}
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                borderRadius: "10px",
                ml: 2,
                width: "98%",
              }}
            >
              <Table sx={{ border: "1px solid #ddd", borderRadius: "10px" }}>
                <TableHead>
                  <TableRow
                    sx={{
                      borderRadius: "10px",
                      backgroundColor: "#1C486B",
                      border: "1px solid #ddd",
                    }}
                  >
                    <TableCell
                      sx={{
                        border: "1px solid #ddd",
                        fontWeight: "bold",
                        color: "#FFFFFF",
                        width: defaultWidth || "10rem",
                      }}
                    >
                      {defaultColumn ? defaultColumn : "Segment"}
                    </TableCell>
                    {section.fields?.map((field) => (
                      <TableCell
                        key={field.fieldName}
                        sx={{
                          border: "1px solid #ddd",
                          fontWeight: "bold",
                          color: "#FFFFFF",
                          width: field.width,
                          maxWidth: field.width,
                        }}
                      >
                        {field.title}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {defaultSegments.map((segment, rowIndex) => (
                    <TableRow key={rowIndex}>
                      <TableCell>{segment.title}</TableCell>
                      {section.fields?.map((field, index) => (
                        <TableCell
                          key={field.fieldName}
                          sx={{
                            border: "1px solid #DBDBDB",
                            py: "0.5rem",
                            width: field.width,
                            maxWidth: field.width,
                            height: "auto",
                          }}
                        >
                          {field.type === "select" ? (
                            <Select
                              multiple
                              disabled={disabled}
                              minRows={1}
                              value={
                                tables[segment?.field]?.[field?.fieldName] || []
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  e,
                                  field.fieldName,
                                  field.title,
                                  segment.field,
                                  segment.title,
                                  field.type,
                                  field.options
                                )
                              }
                              InputProps={{ disableUnderline: true }}
                              displayEmpty
                              fullWidth
                              size="small"
                              sx={{
                                width: field.width,
                                border: "none",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    border: "none",
                                  },
                              }}
                              renderValue={(selected) => {
                                if (selected.includes("Select All")) {
                                  return selected
                                    ?.filter(
                                      (option) => option !== "Select All"
                                    )
                                    ?.join(", "); // Display custom text when "Select All" is selected
                                }
                                return selected.length > 0
                                  ? selected.join(", ")
                                  : field.placeholder || "Select";
                              }}
                            >
                              {field.options?.map((option, idx) => (
                                <MenuItem key={idx} value={option}>
                                  <Checkbox
                                    disabled={disabled}
                                    checked={(
                                      tables[segment?.field]?.[
                                        field?.fieldName
                                      ] || []
                                    ).includes(option)}
                                  />
                                  {option}
                                </MenuItem>
                              ))}
                            </Select>
                          ) : field.type === "radio" ? (
                            <Box>
                              <FormControl sx={{ my: 1, width: field.width }}>
                                <RadioGroup
                                  row
                                  disabled={disabled}
                                  value={
                                    tables[segment?.field]?.[
                                      field?.fieldName
                                    ] || false
                                  }
                                  onChange={(e) =>
                                    handleInputChange(
                                      e,
                                      field.fieldName,
                                      field.title,
                                      segment.field,
                                      segment.title,
                                      field.type
                                    )
                                  }
                                >
                                  <FormControlLabel
                                    value={true}
                                    control={<Radio disabled={disabled} />}
                                    label="Yes"
                                  />
                                  <FormControlLabel
                                    value={false}
                                    control={<Radio disabled={disabled} />}
                                    label="No"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Box>
                          ) : (
                            <TextField
                              disabled={disabled}
                              sx={{ width: field.width }}
                              placeholder={field.placeholder}
                              type={field.type}
                              value={
                                tables[segment?.field]?.[field?.fieldName]
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  e,
                                  field.fieldName,
                                  field.title,
                                  segment.field,
                                  segment.title,
                                  field.type
                                )
                              }
                              multiline
                              minRows={1}
                              size="small"
                              variant="standard"
                              InputProps={{ disableUnderline: true }}
                            />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 1, ml: 2 }}>
              {segmentErrorMessages?.map((errorMessage, idx) => (
                <p
                  key={idx}
                  color="error"
                  variant="caption"
                  style={{
                    lineHeight: "1.2",
                    margin: "2px 0",
                    color: "#d34040",
                    fontSize: "10px",
                    fontStyle: "italic",
                  }}
                >
                  {errorMessage}
                </p>
              ))}
            </Box>
          </div>
        ))}
      </Box>
    );
  }
);

export default TableData;
